// Clinical layer for the doctor dashboard.
//
// The worksheet config (worksheets.js) stays the single source of truth for item
// text. Nothing here restates a label that lives there: red flags are declared by
// the exact checklist label and resolved to their `chk_<index>` position at load,
// so renumbering a checklist can never silently point a flag at the wrong symptom.

import { WORKSHEETS, getWorksheet, STAGES, DOMAIN_ROWS } from "@/config/worksheets";

// ---------- checklist helpers ----------

export function checklistItems(wid) {
  const b = getWorksheet(wid)?.blocks?.find((x) => x.type === "checklist");
  return b?.items || [];
}

// ---------- red flags ----------
// tier: "urgent"  → needs assessment or referral before the programme continues
//       "caution" → must be discussed at this consultation
// `action` is what the clinician should do, not a restatement of the symptom.

export const RED_FLAGS = [
  // --- mind ---
  { wid: "w06", label: "Feeling hopeless or unsafe", tier: "urgent",
    action: "Immediate mental-health assessment. Do not defer to the next review." },
  { wid: "w06", label: "Loss of interest", tier: "caution",
    action: "Screen for depression alongside the Manas score." },

  // --- bleeding / GI ---
  { wid: "w10", label: "Blood in stool", tier: "urgent",
    action: "Examine and refer. Do not attribute to piles without examination." },
  { wid: "w10", label: "Black stool", tier: "urgent",
    action: "Suspect upper-GI bleeding. Urgent referral." },
  { wid: "w28", label: "Blood in stool", tier: "urgent",
    action: "Examine and refer. Do not attribute to piles without examination." },
  { wid: "w28", label: "Black stool", tier: "urgent",
    action: "Suspect upper-GI bleeding. Urgent referral." },
  { wid: "w29", label: "Easy or abnormal bleeding", tier: "urgent",
    action: "Check platelets and clotting before any raktamokshana or blood-thinning herb." },
  { wid: "w11", label: "Gum bleeding", tier: "caution",
    action: "Check for anaemia, vitamin C deficiency or anticoagulant use." },
  { wid: "w09", label: "Vomiting", tier: "caution",
    action: "Establish cause before any vamana or fasting advice." },

  // --- cardiorespiratory ---
  { wid: "w19", label: "Breathlessness", tier: "urgent",
    action: "Assess cardiac and respiratory cause before prescribing exertion or pranayama." },
  { wid: "w29", label: "Breathlessness", tier: "urgent",
    action: "Assess cardiac and respiratory cause before prescribing exertion or pranayama." },
  { wid: "w19", label: "Chest tightness", tier: "urgent",
    action: "Exclude a cardiac cause before it is treated as amlapitta." },
  { wid: "w07", label: "Breathlessness with mild activity", tier: "urgent",
    action: "Exclude anaemia, cardiac and thyroid causes before an activity plan." },
  { wid: "w19", label: "Wheezing", tier: "caution",
    action: "Confirm inhaler technique and adherence before adding herbs." },
  { wid: "w05", label: "Choking or gasping", tier: "urgent",
    action: "Screen for obstructive sleep apnoea. Sleep herbs will not treat this." },
  { wid: "w05", label: "Breathlessness during sleep", tier: "caution",
    action: "Consider cardiac failure and sleep apnoea." },

  // --- neurological ---
  { wid: "w22", label: "Fainting", tier: "urgent",
    action: "Cardiac and neurological assessment before any exertion or fasting advice." },
  { wid: "w22", label: "Loss of balance", tier: "urgent",
    action: "Exclude a central cause. Assess falls risk." },
  { wid: "w22", label: "Weakness", tier: "urgent",
    action: "Exclude a focal neurological deficit." },
  { wid: "w18", label: "Weakness", tier: "urgent",
    action: "With back or neck pain, exclude nerve-root compression before any manipulation." },
  { wid: "w22", label: "Numbness", tier: "caution",
    action: "Map the distribution; check glucose and B12." },
  { wid: "w18", label: "Numbness or tingling", tier: "caution",
    action: "Map the distribution before treating as a purely postural problem." },
  { wid: "w22", label: "New memory concern", tier: "caution",
    action: "Baseline cognitive screen; check thyroid and B12." },

  // --- constitutional ---
  { wid: "w23", label: "Unexplained fever", tier: "urgent",
    action: "Investigate before any programme change." },
  { wid: "w23", label: "Unexplained weight loss", tier: "urgent",
    action: "Investigate. Do not record as a programme success." },
  { wid: "w23", label: "Night sweats", tier: "caution",
    action: "With fever or weight loss, investigate as a triad." },
  { wid: "w07", label: "Sudden unexplained fatigue", tier: "caution",
    action: "Check haemoglobin, glucose and thyroid before a lifestyle explanation." },
  { wid: "w29", label: "Pallor", tier: "caution", action: "Check haemoglobin." },
  { wid: "w23", label: "Known immune disorder", tier: "caution",
    action: "Review every herb and any panchakarma against the condition and its treatment." },

  // --- renal / urinary ---
  { wid: "w30", label: "Reduced urine", tier: "urgent",
    action: "Assess renal function before any diuretic herb or fasting." },
  { wid: "w30", label: "Burning urination", tier: "caution",
    action: "Rule out infection before treating as pitta." },

  // --- women's health ---
  { wid: "w31", label: "Heavy bleeding", tier: "urgent",
    action: "Gynaecological assessment; check haemoglobin." },
  { wid: "w31", label: "Current pregnancy or fertility concern", tier: "urgent",
    action: "Review every herb, fast and panchakarma step for pregnancy safety before continuing." },
  { wid: "w31", label: "Pelvic pain", tier: "caution", action: "Gynaecological assessment." },

  // --- skin / healing ---
  { wid: "w20", label: "Slow wound healing", tier: "caution",
    action: "Check glucose. Consider peripheral vascular assessment." },
  { wid: "w20", label: "Suspected fungal infection", tier: "caution",
    action: "Treat actively; check glucose if recurrent." },
  { wid: "w20", label: "Recurrent boils", tier: "caution",
    action: "Check glucose and consider staphylococcal carriage." },

  // --- medicines & safety ---
  { wid: "w25", label: "Previous adverse reaction", tier: "caution",
    action: "Confirm the agent and reaction before prescribing anything." },
  { wid: "w25", label: "Combining systems without disclosure", tier: "caution",
    action: "Reconcile the full medicine list; check for interactions." },
  { wid: "w25", label: "Stopping prescribed medicine when better", tier: "caution",
    action: "Address directly — this undermines any treatment recorded here." },
  { wid: "w25", label: "Antibiotics without prescription", tier: "caution",
    action: "Counsel on resistance; record what was taken." },
  { wid: "w15", label: "Repeated antibiotic courses", tier: "caution",
    action: "Explains a resistant agni picture. Plan gut restoration accordingly." },
  { wid: "w24", label: "Abnormal report not followed up", tier: "caution",
    action: "Retrieve the report before this consultation ends." },

  // --- eating & substance ---
  { wid: "w13", label: "Binge-like eating", tier: "caution",
    action: "Screen for an eating disorder before prescribing any restriction." },
  { wid: "w13", label: "Eating secretly", tier: "caution",
    action: "Screen for an eating disorder before prescribing any restriction." },
  { wid: "w14", label: "Alcohol use", tier: "caution",
    action: "Quantify intake; check liver-affecting herbs against it." },
  { wid: "w05", label: "Sleeping tablets or alcohol for sleep", tier: "caution",
    action: "Do not stop abruptly. Plan a supervised taper." },

  // --- other ---
  { wid: "w16", label: "Swelling or fluid retention", tier: "caution",
    action: "Assess cardiac, renal and hepatic causes before any fluid advice." },
  { wid: "w17", label: "Medical restriction", tier: "caution",
    action: "Read the restriction before writing any movement plan." },
];

export const TIER_META = {
  urgent: { label: "Urgent", order: 0, ring: "border-rose-300", chip: "bg-rose-600 text-white",
    panel: "bg-rose-50", text: "text-rose-800", dot: "#DC2626" },
  caution: { label: "Discuss today", order: 1, ring: "border-amber-300", chip: "bg-amber-500 text-white",
    panel: "bg-amber-50", text: "text-amber-900", dot: "#D97706" },
};

// ---------- caseload attention ranking ----------
// A triage ordering, not a clinical score: urgent findings dominate, then a
// falling trend, then a low absolute average, then how long the record has sat.
// Exported so the Doctor Panel and the reference screen never fall out of step.
export const ATTENTION_WEIGHTS = {
  urgent: 1000,       // × each urgent finding — always floats to the top
  caution: 60,        // × each "discuss today" finding
  fallingTrend: 40,   // × the size of a drop in overall (per point)
  lowScore: 8,        // × (10 − overall)
  noScore: 40,        // flat weight when there is no overall score yet
  staleness: 0.4,     // × days since update, capped at 120
  staleCap: 120,
};

/** The caseload attention rank for one enriched participant row. */
export function attentionScore({ urgent = 0, caution = 0, delta = null, overall = null, staleDays = null }) {
  const W = ATTENTION_WEIGHTS;
  return (
    urgent * W.urgent +
    caution * W.caution +
    (delta != null && delta < 0 ? -delta * W.fallingTrend : 0) +
    (overall != null ? (10 - overall) * W.lowScore : W.noScore) +
    (staleDays != null ? Math.min(staleDays, W.staleCap) * W.staleness : 0)
  );
}

// { wid: { index: flag } } — resolved once, from the live worksheet config.
export const RED_FLAG_MAP = (() => {
  const map = {};
  const missing = [];
  for (const f of RED_FLAGS) {
    const idx = checklistItems(f.wid).indexOf(f.label);
    if (idx < 0) { missing.push(`${f.wid} · ${f.label}`); continue; }
    if (!map[f.wid]) map[f.wid] = {};
    map[f.wid][idx] = f;
  }
  if (missing.length && process.env.NODE_ENV !== "production")
    console.warn("[clinical] red flags not found in worksheet config:", missing);
  return map;
})();

/**
 * Turn ticked checklist indices into resolved red flags, urgent first.
 * @param ticked  { wid: [index, …] }
 * @param notes   (wid, index) => the participant's own note for that item
 */
export function collectFlags(ticked, notes) {
  const out = [];
  for (const [wid, idxs] of Object.entries(ticked || {})) {
    const perWid = RED_FLAG_MAP[wid];
    if (!perWid) continue;
    const w = getWorksheet(wid);
    for (const i of idxs || []) {
      const f = perWid[i];
      if (!f) continue;
      out.push({ ...f, index: i, worksheetNum: w?.num || "", worksheetTitle: w?.title || wid,
        note: notes ? notes(wid, i) || "" : "" });
    }
  }
  return out.sort((a, b) =>
    TIER_META[a.tier].order - TIER_META[b.tier].order ||
    a.worksheetNum.localeCompare(b.worksheetNum));
}

/** Ticked indices per worksheet, straight from a packed /clinical response set. */
export function tickedFrom(responses) {
  const out = {};
  for (const [wid, r] of Object.entries(responses || {}))
    if (r?.checked?.length) out[wid] = r.checked;
  return out;
}

/** Ticked checklist labels for one worksheet, each with the participant's note. */
export function tickedLabels(responses, wid, only) {
  const r = responses?.[wid];
  if (!r) return [];
  const items = checklistItems(wid);
  return (r.checked || [])
    .filter((i) => (only ? only.includes(i) : true))
    .map((i) => ({ index: i, label: items[i] || `#${i}`, note: r.data?.[`note_${i}`] || "" }))
    .filter((x) => x.label);
}

// Which w02 indices are which kind of history (see worksheet 02's checklist).
export const W02 = {
  conditions: Array.from({ length: 18 }, (_, i) => i),   // 0–17  diagnosed conditions
  history: [18, 19, 26, 27],                             // surgery, injury, hospitalisation, family
  medicines: [20, 21, 22, 23, 25],                       // prescription, herbal, supplements, OTC, antibiotics
  allergies: [24],
};

// ---------- Ayurvedic grouping ----------
// The 28 scored domains regrouped the way a vaidya reads a case, rather than by
// worksheet number. Every scored worksheet appears exactly once.

export const DOMAIN_GROUPS = [
  { key: "agni", label: "Agni & Ahara", sub: "Digestion, appetite and diet",
    wids: ["w08", "w09", "w11", "w12", "w13", "w14", "w15"] },
  { key: "nidra", label: "Nidra", sub: "Sleep and the morning state", wids: ["w04", "w05"] },
  { key: "manas", label: "Manas", sub: "Mind and emotion", wids: ["w06"] },
  { key: "bala", label: "Bala & Ojas", sub: "Energy, immunity and recovery", wids: ["w07", "w23"] },
  { key: "mala", label: "Mala & Vega", sub: "Elimination and natural urges", wids: ["w10", "w27"] },
  { key: "sharira", label: "Sharira", sub: "Body, movement, pain and metabolism",
    wids: ["w16", "w17", "w18", "w19", "w20", "w21", "w22"] },
  { key: "dinacharya", label: "Dinacharya & Prevention", sub: "Daily routine, screening and medicine use",
    wids: ["w24", "w25", "w26"] },
  { key: "srotas", label: "Srotas", sub: "Channel screening", wids: ["w28", "w29", "w30"] },
  { key: "stri", label: "Stri Roga", sub: "Women's health — optional", wids: ["w31"] },
];

export const DOMAIN_LABEL = Object.fromEntries(DOMAIN_ROWS.map((d) => [d.wid, d.label]));
export const SCORED_WIDS = DOMAIN_ROWS.map((d) => d.wid);

/** Per-group averages plus each member domain, for one stage's scores. */
export function groupSummary(scores, responses) {
  return DOMAIN_GROUPS.map((g) => {
    const domains = g.wids.map((wid) => ({
      wid,
      label: DOMAIN_LABEL[wid] || wid,
      num: getWorksheet(wid)?.num || "",
      score: scores?.[wid] ?? null,
      answered: responses?.[wid]?.answered ?? 0,
    }));
    const vals = domains.map((d) => d.score).filter((v) => v != null);
    return {
      ...g,
      domains,
      avg: vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : null,
      scoredCount: vals.length,
    };
  });
}

// ---------- change across stages ----------

/** Stages that actually hold data, in programme order. */
export function presentStages(byStage) {
  return STAGES.filter((s) => byStage && byStage[s] && Object.keys(byStage[s]).length > 0);
}

/**
 * Per-domain movement between the first and latest stage that scored it, plus
 * the step since the previous stage. A drop of 1.5+ is treated as material.
 */
export function domainChanges(byStage) {
  const stages = presentStages(byStage);
  if (!stages.length) return [];
  const latest = stages[stages.length - 1];
  const prev = stages.length > 1 ? stages[stages.length - 2] : null;
  return SCORED_WIDS.map((wid) => {
    const scored = stages.filter((s) => byStage[s]?.[wid] != null);
    const first = scored.length ? byStage[scored[0]][wid] : null;
    const now = byStage[latest]?.[wid] ?? null;
    const before = prev ? byStage[prev]?.[wid] ?? null : null;
    const round1 = (x) => Math.round(x * 10) / 10;
    return {
      wid,
      label: DOMAIN_LABEL[wid] || wid,
      num: getWorksheet(wid)?.num || "",
      firstStage: scored[0] || null,
      first, now, before,
      total: first != null && now != null && scored.length > 1 ? round1(now - first) : null,
      step: before != null && now != null ? round1(now - before) : null,
    };
  });
}

export const MATERIAL_DROP = -1.5;

// ---------- free-text numeric extraction ----------
// Measurements and trackers are free-text cells, so "128/84 mmHg" or "78 kg"
// has to be read leniently. Anything unparseable is shown as entered.

export function num(v) {
  if (v == null) return null;
  const m = String(v).match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

export const MEASURE_ROWS = [
  { i: 0, label: "Height" }, { i: 1, label: "Weight" }, { i: 2, label: "Waist circumference" },
  { i: 3, label: "Body mass index" }, { i: 4, label: "Blood pressure" }, { i: 5, label: "Resting pulse" },
  { i: 6, label: "Oxygen saturation" }, { i: 7, label: "Fasting glucose / HbA1c" },
  { i: 8, label: "Lipid profile" }, { i: 9, label: "Haemoglobin" }, { i: 10, label: "Thyroid function" },
  { i: 11, label: "Other relevant result" },
];

const MEASURE_COLS = ["baseline", "m1", "m2", "m3"];
export const MEASURE_COL_LABELS = ["Baseline", "Month 1", "Month 2", "Month 3"];

/**
 * Worksheet 32 as rows × the four programme columns.
 * `stageData` is w32 data keyed by stage; a later stage's entry for a column
 * wins, so a value filled in at Month 2 is not hidden by an empty Baseline copy.
 */
export function measureTable(stageData) {
  const merged = {};
  for (const s of STAGES) Object.assign(merged, stageData?.[s] || {});
  return MEASURE_ROWS.map((r) => {
    const cells = MEASURE_COLS.map((c) => (merged[`meas_${r.i}_${c}`] || "").trim());
    return { ...r, cells, empty: cells.every((c) => !c) };
  });
}

const TREND_ROWS = ["Baseline", ...Array.from({ length: 12 }, (_, i) => `Week ${i + 1}`)];

/** Worksheet 33: up to three named symptoms scored 0–10 over 13 points. */
export function symptomTrend(stageData) {
  const merged = {};
  for (const s of STAGES) Object.assign(merged, stageData?.[s] || {});
  return [1, 2, 3].map((n) => {
    const points = TREND_ROWS.map((label, r) => ({
      label,
      value: num(merged[`trk_${r}_s${n}`]),
      trigger: (merged[`trk_${r}_trigger`] || "").trim(),
      note: (merged[`trk_${r}_note`] || "").trim(),
    }));
    const filled = points.filter((p) => p.value != null);
    return {
      name: (merged[`sym${n}name`] || "").trim(),
      points,
      first: filled.length ? filled[0].value : null,
      last: filled.length ? filled[filled.length - 1].value : null,
      count: filled.length,
    };
  }).filter((s) => s.name || s.count > 0);
}

const PLAN_ROWS = ["Morning", "Food / gut", "Workplace", "Evening / sleep"];
const FOLLOW_UPS = [{ wid: "w36", label: "Month 1" }, { wid: "w37", label: "Month 2" }, { wid: "w38", label: "Month 3" }];

/**
 * The 90-day plan (w35) against each month's adherence review (w36–38).
 * Without adherence, a flat score reads as "treatment failed" when it may be
 * "never attempted" — that distinction is the point of this table.
 */
export function adherenceTable(worksheetsByStage) {
  const pick = (wid) => {
    for (const s of [...STAGES].reverse()) {
      const d = worksheetsByStage?.[s]?.[wid];
      if (d && Object.keys(d).length) return d;
    }
    return null;
  };
  const plan = pick("w35") || {};
  const reviews = FOLLOW_UPS.map((f) => ({ ...f, data: pick(f.wid) })).filter((f) => f.data);

  const rows = PLAN_ROWS.map((label, r) => ({
    label,
    action: (plan[`act_${r}_action`] || "").trim(),
    often: (plan[`act_${r}_often`] || "").trim(),
    barrier: (plan[`act_${r}_barrier`] || "").trim(),
    solution: (plan[`act_${r}_solution`] || "").trim(),
    reviews: reviews.map((f) => ({
      month: f.label,
      planned: (f.data[`fua_${r}_planned`] || "").trim(),
      adherence: num(f.data[`fua_${r}_adherence`]),
      benefit: (f.data[`fua_${r}_benefit`] || "").trim(),
      decision: (f.data[`fua_${r}_decision`] || "").trim(),
    })),
  }));

  const all = rows.flatMap((r) => r.reviews.map((v) => v.adherence)).filter((v) => v != null);
  return {
    rows,
    months: reviews.map((f) => f.label),
    mean: all.length ? Math.round((all.reduce((a, b) => a + b, 0) / all.length) * 10) / 10 : null,
    hasPlan: rows.some((r) => r.action),
  };
}

/** Adherence 0–10 read the same way as a domain score. */
export function adherenceTone(v) {
  if (v == null) return { color: "#78716c", label: "—" };
  if (v < 4) return { color: "#DC2626", label: "Not followed" };
  if (v < 7) return { color: "#D97706", label: "Partly followed" };
  return { color: "#15803D", label: "Followed" };
}

// ---------- completeness ----------

/**
 * A domain scores null when 7 of 8 statements are answered — identical on screen
 * to one never started. This separates the two so gaps can be filled during the
 * consultation rather than discovered at the end of the programme.
 */
export function completeness(responses) {
  const partial = [], notStarted = [];
  let scoredCount = 0;
  for (const wid of SCORED_WIDS) {
    const r = responses?.[wid];
    const answered = r?.answered ?? 0;
    if (r?.domain_score != null) { scoredCount += 1; continue; }
    const entry = { wid, label: DOMAIN_LABEL[wid] || wid, num: getWorksheet(wid)?.num || "", answered };
    if (answered > 0) partial.push(entry); else notStarted.push(entry);
  }
  return { total: SCORED_WIDS.length, scoredCount, partial, notStarted };
}

// ---------- worksheet 01 / 03 extraction ----------

export function identity(responses) {
  const w1 = responses?.w01?.data || {};
  return {
    fullName: w1.full_name || "",
    code: w1.participant_code || "",
    age: w1.age_dob || "",
    sex: w1.sex || "",
    contact: w1.contact || "",
    dept: w1.dept || "",
    prakriti: w1.prakriti || "",
    workHours: w1.work_hours || "",
    shift: w1.shift || "",
    years: w1.years_occ || "",
    daysOff: w1.days_off || "",
    commute: w1.commute || "",
  };
}

/** Worksheet 03 read back as its two tables: main concerns, then 90-day goals. */
export function concerns(responses) {
  const data = responses?.w03?.data || {};
  const table = (key) => {
    const b = getWorksheet("w03")?.blocks?.find((x) => x.type === "rowtable" && x.key === key);
    if (!b) return { columns: [], rows: [] };
    const rows = [];
    for (let r = 0; r < (b.rowLabels?.length || b.rows || 0); r += 1) {
      const cells = b.columns.map((c) => (data[`${key}_${r}_${c.key}`] || "").trim());
      if (cells.some(Boolean)) rows.push(cells);
    }
    return { title: b.title, columns: b.columns.map((c) => c.label), rows };
  };
  return { concerns: table("concern"), goals: table("goal"), other: (data.other || "").trim() };
}

export { STAGES, WORKSHEETS };
